import React, { useState, useEffect } from 'react';
import { renderTimeline } from '../../services/renderService';
import { startCloudRender, pollRenderUntilComplete, saveProjectToBackend, CONFIG } from '../../services/backendService';
import { CloseIcon } from '../icons/CloseIcon';
import { LoadingSpinner } from '../icons/LoadingSpinner';
import { DownloadIcon } from '../icons/DownloadIcon';
import { FilmIcon } from '../icons/FilmIcon';
import { SaveIcon } from '../icons/SaveIcon';
import { v4 as uuidv4 } from 'uuid';
import { useUser } from '../../context/UserContext';
import { Cloud, Clock, Sparkles, CheckCircle, AlertTriangle, Home, Clapperboard } from 'lucide-react';
import { RENDER_COST_PER_CLIP } from '../../types';

export const RenderModal = ({ project, onClose, onProjectUpdate, onGoToDashboard }) => {
    const [status, setStatus] = useState('idle');
    const [progress, setProgress] = useState(0);
    const [message, setMessage] = useState('');
    const [videoUrl, setVideoUrl] = useState(null);
    const [error, setError] = useState(null);
    const [isSavingToProject, setIsSavingToProject] = useState(false);
    const [elapsedTime, setElapsedTime] = useState(0);
    // Credits charged + remaining balance, captured at render-complete time so
    // the post-render summary stays stable even if the header balance later
    // changes (e.g., the user starts another generation while this modal is open).
    const [renderCreditsCharged, setRenderCreditsCharged] = useState(null);
    const [renderCreditsRemaining, setRenderCreditsRemaining] = useState(null);
    
    const { user, refreshCredits } = useUser();
    const userEmail = user?.email || 
                      sessionStorage.getItem('userEmail') || 
                      localStorage.getItem('user_email') || 
                      CONFIG.userEmail || 
                      'guest@loopai.com';

    useEffect(() => {
        return () => {
            if (videoUrl && videoUrl.startsWith('blob:')) URL.revokeObjectURL(videoUrl);
        };
    }, [videoUrl]);

    // Elapsed time counter during rendering
    useEffect(() => {
        if (status === 'cloud-uploading' || status === 'cloud-processing' || status === 'rendering') {
            setElapsedTime(0);
            const timer = setInterval(() => setElapsedTime(t => t + 1), 1000);
            return () => clearInterval(timer);
        }
    }, [status]);

    const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

    const clipCount = project.timeline?.clips?.length || 0;
    const duration = project.timeline?.duration || 0;

    // Render cost: see RENDER_COST_PER_CLIP in /src/types.js — shared with
    // the Tier Selection page so estimates and the actual charge match.
    const numVideoClips = (project.timeline?.clips || []).filter(c => c.type === 'video').length;
    const totalRenderCost = numVideoClips * RENDER_COST_PER_CLIP;
    const currentCredits = user?.credits ?? 0;
    const balanceAfter = currentCredits - totalRenderCost;
    const insufficient = balanceAfter < 0;

    const handleCloudRender = async () => {
        if (!project.timeline || clipCount === 0) {
            setError("Cannot render an empty timeline.");
            setStatus('error');
            return;
        }
        // Block render if the user can't afford it (banner is also shown).
        if (insufficient) {
            setError(`You need ${totalRenderCost} credits to render this project (${numVideoClips} video clips × ${RENDER_COST_PER_CLIP}). Current balance: ${currentCredits}.`);
            setStatus('error');
            return;
        }

        // Diagnostic: break down what's on the timeline at render click
        const clipsByType = {};
        (project.timeline.clips || []).forEach(c => {
            clipsByType[c.type] = (clipsByType[c.type] || 0) + 1;
        });
        console.log('🎬 Render clicked. Timeline breakdown:', clipsByType, `(total ${clipCount} clips)`);
        const overlays = (project.timeline.clips || []).filter(
            c => c.type === 'text' || c.type === 'logo' || c.type === 'image'
        );
        console.log(`🎨 Overlays in timeline at render time: ${overlays.length}`,
            overlays.length > 0 ? overlays.map(c => `${c.type}@${c.start}s`) : '(none)');

        setStatus('cloud-uploading');
        setError(null);
        setMessage("Saving project & uploading assets...");

        try {
            const saveResult = await saveProjectToBackend(project, userEmail);
            if (!saveResult.success) throw new Error(saveResult.error || "Failed to upload project assets.");
            
            const projectForRender = saveResult.optimizedProject || project;
            setMessage("Starting cloud render job...");

            let startResult;
            try {
                startResult = await startCloudRender(projectForRender, userEmail);
            } catch (renderError) {
                throw new Error(renderError.message);
            }
            
            if (!startResult.success || !startResult.jobId) throw new Error(startResult.error || "Failed to start cloud render.");

            setStatus('cloud-processing');
            setMessage("Job queued. Cloud server is processing...");

            const pollResult = await pollRenderUntilComplete(
                startResult.jobId, userEmail,
                (statusUpdate) => {
                    if (statusUpdate.status === 'processing') setMessage("Rendering in progress... Almost there.");
                    else if (statusUpdate.status === 'pending' || statusUpdate.status === 'queued') setMessage("Queued for rendering...");
                },
                300
            );

            if (pollResult.success && pollResult.url) {
                setVideoUrl(pollResult.url);
                setStatus('done');
                // Refresh the header balance pill from the backend so the user
                // sees the live post-render credit balance immediately.
                try {
                    const fresh = refreshCredits ? await refreshCredits() : null;
                    const creditsAfter = (typeof fresh === 'number')
                        ? fresh
                        : (fresh?.credits ?? null);
                    // Capture the snapshot for the post-render summary.
                    setRenderCreditsCharged(totalRenderCost);
                    setRenderCreditsRemaining(
                        creditsAfter !== null ? creditsAfter : Math.max(0, currentCredits - totalRenderCost)
                    );
                } catch (e) {
                    console.warn('Post-render credit refresh failed:', e?.message || e);
                    // Fall back to optimistic local math so the summary still renders.
                    setRenderCreditsCharged(totalRenderCost);
                    setRenderCreditsRemaining(Math.max(0, currentCredits - totalRenderCost));
                }
            } else {
                throw new Error(pollResult.error || "Cloud rendering failed.");
            }
        } catch (err) {
            setError(err.message || "Failed to render video.");
            setStatus('error');
        }
    };
    
    const [isDownloading, setIsDownloading] = useState(false);

    const handleDownload = async () => {
        if (!videoUrl || isDownloading) return;
        setIsDownloading(true);
        const filename = `${(project.name || 'video').replace(/[^a-zA-Z0-9-_]/g, '_')}_render.mp4`;

        try {
            // blob: URLs are same-origin, `download` works directly
            if (videoUrl.startsWith('blob:')) {
                const a = document.createElement('a');
                a.href = videoUrl;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                return;
            }

            // Cross-origin (GCS signed URL): fetch → Blob → blob: URL → trigger download
            const response = await fetch(videoUrl, { credentials: 'omit' });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            // Revoke on next tick so the browser has time to start the download
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        } catch (err) {
            console.error('Download failed:', err);
            // Last-resort fallback: open the URL in a new tab so the user can right-click → Save As
            window.open(videoUrl, '_blank', 'noopener,noreferrer');
        } finally {
            setIsDownloading(false);
        }
    };

    const handleSaveToProject = async () => {
        if (!videoUrl) return;
        setIsSavingToProject(true);
        try {
            const newAsset = { id: `asset-video-${uuidv4()}`, type: 'video', name: `Rendered Video - ${new Date().toLocaleString()}.mp4`, data: videoUrl };
            const updatedProject = { ...project, assets: [...project.assets, newAsset] };
            onProjectUpdate(updatedProject);
            const result = await saveProjectToBackend(updatedProject, userEmail);
            if (result.success) {
                if (result.optimizedProject) onProjectUpdate(result.optimizedProject);
                setStatus('saved');
            } else {
                throw new Error(result.error);
            }
        } catch (e) {
            console.error(e);
            alert(`Failed to save: ${e.message}`);
        } finally {
            setIsSavingToProject(false);
        }
    };

    const renderContent = () => {
        switch (status) {
            case 'idle':
                return (
                    <div className="space-y-6">
                        {/* Project summary */}
                        <div className="grid grid-cols-3 gap-3">
                            <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-center">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Clips</p>
                                <p className="text-lg font-bold text-white mt-0.5">{clipCount}</p>
                            </div>
                            <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-center">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Duration</p>
                                <p className="text-lg font-bold text-white mt-0.5">{duration.toFixed(1)}s</p>
                            </div>
                            <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-center">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Ratio</p>
                                <p className="text-lg font-bold text-white mt-0.5">{project.aspectRatio || '16:9'}</p>
                            </div>
                        </div>

                        {/* Cost summary */}
                        <div className={`rounded-xl px-4 py-3 border ${insufficient ? 'bg-amber-500/10 border-amber-500/40' : 'bg-violet-600/10 border-violet-500/30'}`}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">Render Cost</p>
                                    <p className="text-base font-bold text-white mt-0.5" data-testid="render-cost-label">
                                        {totalRenderCost} credits
                                        <span className="text-zinc-400 font-normal text-xs ml-2">
                                            ({numVideoClips} video clip{numVideoClips === 1 ? '' : 's'} × {RENDER_COST_PER_CLIP})
                                        </span>
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">Balance After</p>
                                    <p className={`text-base font-bold mt-0.5 ${insufficient ? 'text-amber-400' : 'text-emerald-400'}`} data-testid="render-balance-after">
                                        {balanceAfter.toLocaleString()}
                                    </p>
                                </div>
                            </div>
                            {insufficient && (
                                <div className="mt-3 pt-3 border-t border-amber-500/20" data-testid="insufficient-credits-banner">
                                    <p className="text-xs text-amber-300">
                                        You need {totalRenderCost} credits to render ({numVideoClips} clips × {RENDER_COST_PER_CLIP}). Current balance: {currentCredits.toLocaleString()}.
                                    </p>
                                    <button
                                        onClick={() => window.location.href = 'https://app.loopai.in'}
                                        className="mt-2 text-xs font-semibold text-amber-300 underline hover:text-amber-200"
                                        data-testid="insufficient-credits-topup-btn"
                                    >
                                        Top up credits →
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Cloud render button */}
                        <button
                            onClick={handleCloudRender}
                            disabled={insufficient}
                            className={`w-full font-bold py-4 px-6 rounded-xl text-sm flex items-center justify-center transition-all hover:-translate-y-0.5 ${
                                insufficient
                                    ? 'bg-zinc-700 text-zinc-400 cursor-not-allowed hover:translate-y-0'
                                    : 'bg-violet-600 hover:bg-violet-700 text-white'
                            }`}
                            data-testid="cloud-render-btn"
                            title={insufficient ? 'Insufficient credits' : `Render this project for ${totalRenderCost} credits`}
                        >
                            <Cloud className="w-5 h-5 mr-2" />
                            {insufficient
                                ? `Need ${Math.abs(balanceAfter)} more credits`
                                : `Render (${totalRenderCost} credits — ${numVideoClips} clip${numVideoClips === 1 ? '' : 's'} × ${RENDER_COST_PER_CLIP})`}
                        </button>

                        {/* Info cards */}
                        <div className="space-y-2">
                            <div className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                                <Sparkles className="w-4 h-4 text-violet-400 mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-xs font-semibold text-zinc-300">Best Quality Output</p>
                                    <p className="text-[11px] text-zinc-500">Cloud rendering produces studio-grade video with all effects, transitions, and overlays.</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                                <Clock className="w-4 h-4 text-violet-400 mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-xs font-semibold text-zinc-300">Estimated Time: 3-5 minutes</p>
                                    <p className="text-[11px] text-zinc-500">Rendering time depends on video duration and complexity. You can close this and come back.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'rendering':
            case 'cloud-uploading':
            case 'cloud-processing':
                return (
                    <div className="text-center py-6 space-y-5">
                        <div className="w-16 h-16 mx-auto rounded-2xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
                            <LoadingSpinner className="w-8 h-8 text-violet-400" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-white mb-1">
                                {status === 'cloud-uploading' ? 'Uploading Assets...' : 'Rendering Your Video...'}
                            </h3>
                            <p className="text-sm text-zinc-400">{message}</p>
                        </div>
                        {/* Always show progress bar */}
                        <div className="w-full bg-zinc-800 rounded-full h-2 overflow-hidden">
                            {progress > 0 ? (
                                <div className="bg-violet-500 h-full transition-all duration-500 rounded-full" style={{ width: `${progress}%` }} />
                            ) : (
                                <div className="bg-violet-500 h-full rounded-full animate-pulse" style={{ width: status === 'cloud-uploading' ? '30%' : '60%' }} />
                            )}
                        </div>
                        <div className="flex items-center justify-center gap-4 text-[11px] text-zinc-500">
                            <span>Elapsed: {formatTime(elapsedTime)}</span>
                            <span>Estimated: 3-5 min</span>
                        </div>
                        <p className="text-[11px] text-zinc-600">Please don't close this window.</p>
                    </div>
                );

            case 'done':
                return (
                    <div className="space-y-5">
                        <div className="text-center">
                            <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                            <h3 className="text-lg font-bold text-white">Render Complete</h3>
                            <p className="text-xs text-zinc-500">Your video is ready to download or save.</p>
                        </div>
                        {/* Post-render credit summary */}
                        {renderCreditsCharged !== null && (
                            <div
                                className="flex items-center justify-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-2.5 text-sm"
                                data-testid="render-credit-summary"
                            >
                                <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                                <span className="text-zinc-300">
                                    Render complete · <span className="font-semibold text-white">{renderCreditsCharged.toLocaleString()} credits charged</span>
                                    {renderCreditsRemaining !== null && (
                                        <> · <span className="font-semibold text-white">{renderCreditsRemaining.toLocaleString()} credits remaining</span></>
                                    )}
                                </span>
                            </div>
                        )}
                        {videoUrl && (
                            <video src={videoUrl} controls className="w-full rounded-xl border border-white/10" />
                        )}
                        <div className="flex gap-3">
                            <button
                                onClick={handleDownload}
                                disabled={isDownloading}
                                className="flex-1 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-xl flex items-center justify-center transition-all text-sm"
                                data-testid="download-video-btn"
                            >
                                {isDownloading ? (
                                    <>
                                        <LoadingSpinner className="w-4 h-4 mr-2" />
                                        Downloading...
                                    </>
                                ) : (
                                    <>
                                        <DownloadIcon className="w-4 h-4 mr-2" />
                                        Download
                                    </>
                                )}
                            </button>
                            <button
                                onClick={handleSaveToProject}
                                disabled={isSavingToProject}
                                className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-50 text-white font-bold py-3 px-4 rounded-xl flex items-center justify-center transition-all text-sm"
                                data-testid="save-to-project-btn"
                            >
                                <SaveIcon className="w-4 h-4 mr-2" />
                                {isSavingToProject ? 'Saving...' : 'Save to Project'}
                            </button>
                        </div>
                    </div>
                );

            case 'error':
                return (
                    <div className="text-center py-6 space-y-4">
                        <AlertTriangle className="w-10 h-10 text-red-400 mx-auto" />
                        <div>
                            <h3 className="text-lg font-bold text-white mb-1">Render Failed</h3>
                            <p className="text-sm text-zinc-400">{error}</p>
                        </div>
                        <button
                            onClick={() => setStatus('idle')}
                            className="bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold py-2.5 px-6 rounded-xl text-sm transition-all"
                        >
                            Try Again
                        </button>
                    </div>
                );

            case 'saved':
                return (
                    <div className="text-center py-6 space-y-5">
                        <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto" />
                        <div>
                            <h3 className="text-lg font-bold text-white mb-1">Video Saved Successfully</h3>
                            <p className="text-sm text-zinc-400">Your rendered video has been saved to the project dashboard.</p>
                        </div>
                        <p className="text-xs text-zinc-500">Where would you like to go next?</p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => window.location.href = 'https://app.loopai.in'}
                                className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold py-3 px-4 rounded-xl flex items-center justify-center transition-all text-sm"
                                data-testid="go-user-dashboard-btn"
                            >
                                <Home className="w-4 h-4 mr-2" />
                                User Dashboard
                            </button>
                            <button
                                onClick={() => { onClose(); if (onGoToDashboard) onGoToDashboard(); }}
                                className="flex-1 bg-violet-600 hover:bg-violet-700 text-white font-bold py-3 px-4 rounded-xl flex items-center justify-center transition-all text-sm"
                                data-testid="go-studio-dashboard-btn"
                            >
                                <Clapperboard className="w-4 h-4 mr-2" />
                                Studio Pro
                            </button>
                        </div>
                        <button
                            onClick={() => setStatus('done')}
                            className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors underline"
                        >
                            Back to video preview
                        </button>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="rounded-2xl border border-white/10 w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col shadow-[0_8px_40px_rgba(0,0,0,0.5)]" style={{ background: '#0F1115' }} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="px-6 py-4 flex justify-between items-center border-b border-white/10">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
                            <FilmIcon className="w-4 h-4 text-violet-400" />
                        </div>
                        <h2 className="text-sm font-bold text-white">Export Video</h2>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
                        <CloseIcon className="w-4 h-4 text-zinc-400" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 flex-grow overflow-y-auto">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};
